import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AuthApi } from "../../../api/common/apiIndex";
import Loader from "../commonUtils/Loader";

export default function ChangePassword({ className = "", onSuccess }) {
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });

  const getPasswordStrength = useMemo(() => (p = "") => {
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score >= 4) return { score, label: "Strong", cls: "text-success", bar: "bg-success", pct: 100 };
    if (score >= 3) return { score, label: "Medium", cls: "text-warning", bar: "bg-warning", pct: 66 };
    if (score >= 1) return { score, label: "Weak", cls: "text-danger", bar: "bg-danger", pct: 33 };
    return { score, label: "", cls: "", bar: "bg-secondary-subtle", pct: 0 };
  }, []);

  const dirty = useMemo(
    () => !!(pw.currentPassword || pw.newPassword || pw.confirm),
    [pw]
  );

  const valid = useMemo(() => {
    const hasCurrent = !!pw.currentPassword;
    const longEnough = pw.newPassword.length >= 8;
    const different = pw.newPassword && pw.currentPassword && pw.newPassword !== pw.currentPassword;
    const matches = pw.newPassword && pw.newPassword === pw.confirm;
    return hasCurrent && longEnough && different && matches;
  }, [pw]);

  const changePassword = useMutation({
    mutationFn: (payload) => AuthApi.changePassword(payload),
    onSuccess: () => {
      toast.success("Password changed");
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
      onSuccess?.();
    },
    onError: (e) => toast.error(e?.response?.data?.message || "Password change failed"),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!valid) {
      if (!pw.currentPassword) toast.warn("Enter your current password");
      else if (pw.newPassword.length < 8) toast.warn("New password must be at least 8 characters");
      else if (pw.newPassword === pw.currentPassword) toast.warn("New password must be different from current");
      else if (pw.newPassword !== pw.confirm) toast.warn("New passwords do not match");
      return;
    }
    changePassword.mutate({
      currentPassword: pw.currentPassword,
      newPassword: pw.newPassword,
    });
  };

  const s = getPasswordStrength(pw.newPassword);
  const canSubmit = dirty && valid && !changePassword.isPending;

  return (
    <div className={`border rounded-4 p-3 p-md-4 position-relative ${className}`}>
      <Loader variant="overlay" size="sm" text="Changing…" show={changePassword.isPending} backdrop={false} />

      <h6 className="mb-3">Change password</h6>
      <form className="row g-3" onSubmit={onSubmit}>
        <div className="col-md-6">
          <label className="form-label">Current password</label>
          <input
            type="password"
            className="form-control"
            placeholder="••••••••"
            autoComplete="current-password"
            value={pw.currentPassword}
            onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
            disabled={changePassword.isPending}
          />
        </div>
        <div className="col-md-6 d-none d-md-block" />
        <div className="col-md-6">
          <label className="form-label">New password</label>
          <input
            type="password"
            className="form-control"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            value={pw.newPassword}
            onChange={(e) => setPw({ ...pw, newPassword: e.target.value })}
            disabled={changePassword.isPending}
          />
          {!!s.label && <small className={`mt-1 d-inline-block ${s.cls}`}>Strength: {s.label}</small>}
          <div className="progress mt-1" style={{ height: 6 }} aria-hidden="true">
            <div className={`progress-bar ${s.bar}`} style={{ width: `${s.pct}%` }} />
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label">Confirm new password</label>
          <input
            type="password"
            className="form-control"
            placeholder="Repeat new password"
            autoComplete="new-password"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            disabled={changePassword.isPending}
          />
          {pw.confirm && pw.confirm !== pw.newPassword && (
            <small className="text-danger d-block mt-1">Passwords do not match.</small>
          )}
        </div>
        <div className="col-12">
          <button
            className="btn btn-outline-primary rounded-pill"
            disabled={!canSubmit}
            aria-busy={changePassword.isPending}
          >
            Change password
          </button>
        </div>
      </form>
    </div>
  );
}
